import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Printer, XCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PassIssueSheet } from "./PassIssueSheet";
import { PassPrintView } from "./PassPrintView";
import {
  PressPass,
  JournalistOption,
  PassStatus,
  PassType,
  TERM_BADGE_CLASSES,
  TERM_LABELS,
} from "./types";

const STATUS_CONFIG: Record<PassStatus, { label: string; className: string }> = {
  active: { label: "Active", className: "bg-green-100 text-green-800 border-green-300" },
  expired: { label: "Expired", className: "bg-gray-100 text-gray-600 border-gray-300" },
  revoked: { label: "Revoked", className: "bg-red-100 text-red-800 border-red-300" },
};

interface PressPassesTabProps {
  orgId: string;
  journalists: JournalistOption[];
  preselectedJournalistId?: string;
  onPassIssued?: () => void;
}

export function PressPassesTab({
  orgId,
  journalists,
  preselectedJournalistId,
  onPassIssued,
}: PressPassesTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [issueOpen, setIssueOpen] = useState(!!preselectedJournalistId);
  const [printPass, setPrintPass] = useState<PressPass | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PressPass | null>(null);
  const [revoking, setRevoking] = useState(false);

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");

  const { data: org } = useQuery({
    queryKey: ["press_passes_org", orgId],
    queryFn: async () => {
      const { data } = await supabase
        .from("organizations" as any)
        .select("name, logo_url, brand_primary, brand_accent")
        .eq("id", orgId)
        .maybeSingle();
      return data as any;
    },
    enabled: !!orgId,
  });

  const { data: passes = [], isLoading } = useQuery({
    queryKey: ["press_passes", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("press_passes" as any)
        .select(`
          *,
          journalist:journalist_id (first_name, last_name, nationality, photo_url, verification_id, position)
        `)
        .eq("org_id", orgId)
        .order("issued_at", { ascending: false });
      if (error) throw error;
      return (data as any[]).map((p) => ({
        ...p,
        outlet_name:
          journalists.find((j) => j.id === p.journalist_id)?.outlet_name ?? null,
      })) as PressPass[];
    },
    enabled: !!orgId,
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ["press_passes", orgId] });
  }

  async function handleRevoke(pass: PressPass) {
    setRevoking(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("press_passes" as any)
        .update({
          status: "revoked",
          revoked_by: user?.id ?? null,
          revoked_at: new Date().toISOString(),
        })
        .eq("id", pass.id);
      if (error) throw error;
      toast({ title: `Pass ${pass.pass_number} revoked` });
      refresh();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setRevoking(false);
      setRevokeTarget(null);
    }
  }

  const filtered = passes.filter((p) => {
    const journalist = p.journalist as any;
    const name = journalist
      ? `${journalist.first_name} ${journalist.last_name}`
      : "";
    const matchSearch =
      !search ||
      name.toLowerCase().includes(search.toLowerCase()) ||
      p.pass_number.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || p.status === filterStatus;
    const matchType = filterType === "all" || p.pass_type === filterType;
    return matchSearch && matchStatus && matchType;
  });

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={() => setIssueOpen(true)}
          style={{ backgroundColor: "#006B3C" }}
          className="text-white hover:opacity-90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Issue New Pass
        </Button>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search journalist or pass number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="revoked">Revoked</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="annual">Annual</SelectItem>
            <SelectItem value="session">Session</SelectItem>
            <SelectItem value="day">Day</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: "#0D2240" }}>
              {["Pass Number", "Journalist", "Type", "Valid", "Status", "Actions"].map(
                (h) => (
                  <TableHead
                    key={h}
                    style={{ color: "#F0E4B8" }}
                    className="font-semibold"
                  >
                    {h}
                  </TableHead>
                )
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-12 text-muted-foreground"
                >
                  No press passes found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((pass, idx) => {
                const journalist = pass.journalist as any;
                const name = journalist
                  ? `${journalist.first_name} ${journalist.last_name}`
                  : "";
                const isExpired = new Date(pass.valid_to) < new Date();
                const statusConf =
                  STATUS_CONFIG[pass.status as PassStatus] ?? STATUS_CONFIG.active;
                const validFrom = new Date(pass.valid_from).toLocaleDateString(
                  "en-GB",
                  { day: "2-digit", month: "short", year: "numeric" }
                );
                const validTo = new Date(pass.valid_to).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                });

                return (
                  <TableRow
                    key={pass.id}
                    className={idx % 2 === 1 ? "bg-[#F7F5F0]" : ""}
                  >
                    <TableCell>
                      <span
                        className="font-mono font-semibold text-sm"
                        style={{ color: "#006B3C" }}
                      >
                        {pass.pass_number}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{name}</div>
                      {pass.outlet_name && (
                        <div className="text-xs text-muted-foreground">
                          {pass.outlet_name}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {pass.accreditation_term ? (
                        <Badge
                          variant="outline"
                          className={TERM_BADGE_CLASSES[pass.accreditation_term]}
                        >
                          {TERM_LABELS[pass.accreditation_term]}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="capitalize">
                          {pass.pass_type}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`text-sm ${
                          isExpired && pass.status === "active"
                            ? "text-red-600"
                            : ""
                        }`}
                      >
                        {validFrom} → {validTo}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusConf.className}>
                        {statusConf.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPrintPass(pass)}
                        >
                          <Printer className="w-3.5 h-3.5" />
                        </Button>
                        {pass.status === "active" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => setRevokeTarget(pass)}
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Revoke confirmation */}
      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={(o) => !o && setRevokeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Press Pass?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revoke pass{" "}
              <span className="font-mono font-semibold">
                {revokeTarget?.pass_number}
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={revoking}
              onClick={() => revokeTarget && handleRevoke(revokeTarget)}
            >
              {revoking && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Revoke Pass
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pass issue sheet */}
      <PassIssueSheet
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        onIssued={() => {
          refresh();
          onPassIssued?.();
        }}
        journalists={journalists}
        orgId={orgId}
        preselectedJournalistId={preselectedJournalistId}
      />

      {/* Press pass print view */}
      {printPass && (
        <PassPrintView
          pass={printPass}
          organization={org ?? null}
          canPrint
          onClose={() => setPrintPass(null)}
        />
      )}
    </div>
  );
}
